import { Close as CloseIcon } from '@mui/icons-material';
import type { Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { useDrop } from 'react-dnd';
import type { ComponentDefinition } from '@/admins/SmartReports/components/Components/types';
import type { SmartReportComponentTypesEnum } from '@/admins/SmartReports/types';

const useStyles = makeStyles((theme: Theme) => ({
  dropzone: {
    position: 'relative',
    display: 'flex',
    width: '100%',
    height: '100%',
    overflow: 'auto',

    '&:hover $deleteIcon': {
      opacity: 1,
      transition: 'opacity 0.2s var(--ease-enter)',
    },
  },
  isActive: {
    backgroundColor: theme.palette.baseColors.deepSeaNavy.c25,
  },
  canDrop: {
    backgroundColor: theme.palette.baseColors.skyBlue.c25,
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.7)',
    zIndex: 1,
    fontWeight: 'bold',
    fontSize: '1.2rem',
  },
  deleteIcon: {
    position: 'absolute',
    top: 0,
    right: 0,
    backgroundColor: `${theme.palette.baseColors.white.c100}`,
    color: theme.palette.baseColors.errorRed.c100,
    border: `0.125rem solid ${theme.palette.baseColors.errorRed.c100}`,
    borderRadius: '50%',
    padding: '0.125rem',
    cursor: 'pointer',
    opacity: 0,
    transition: 'opacity 0.2s var(--ease-exit)',
  },
}));

type DropContainerProps = {
  accept: SmartReportComponentTypesEnum[];
  children: React.ReactNode;
  onDrop: (draggedComponent: ComponentDefinition<unknown>) => void;
  onRemove?: (e: React.MouseEvent) => void;
};

export const DropContainer = ({ accept, children, onDrop, onRemove }: DropContainerProps) => {
  const classes = useStyles();

  const [{ isOver, canDrop }, drop] = useDrop({
    accept,
    drop: onDrop,
    collect: monitor => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  const isActive = isOver && canDrop;
  return (
    <div
      ref={drop}
      className={clsx(classes.dropzone, {
        [classes.canDrop]: canDrop,
        [classes.isActive]: isActive,
      })}
    >
      {children}
      {isActive && <span className={classes.overlay}>Release to drop</span>}
      {!!onRemove && <CloseIcon className={classes.deleteIcon} onClick={onRemove} />}
    </div>
  );
};
